import { EventInput, LocationType } from '@amzn/red-velvet-api';
import { GRANT_ID_REGEX } from './eventValidation';
import { FIELD_LIMITS } from './fieldConstraints';
import { FOCUS_AREAS, PRIMARY_ACTIVITIES } from '../data/eventOptionLists';

/**
 * Deep-link prefill for event creation (VOLUNTEERCENTER-777).
 *
 * A contextual link (grant-approval email, campaign CTA, site QR code, partner toolkit) can carry an
 * allowlisted set of query params that seed the create-event form. This module is the single,
 * validated parser: it maps only known params to event fields, validates each against existing field
 * rules, and silently drops anything invalid or unknown — prefill must never block or error the flow.
 *
 * IMPORTANT (VC-777 Option A): the param names below are the SAME legacy names that
 * `useEditableEventInfo` (the edit-event / track-attendance flow) already reads from the URL, and the
 * VALUE formats match what that hook consumes — so a SINGLE link works identically across create-event
 * and the edit/attendance flows:
 *   - `focus` / `activity`         → eventOptionLists codes (e.g. `stem`, `mentoring-tutoring`)
 *   - `activity_location_type`     → `on-campus` | `off-campus` | `virtual`
 *   - `building_code`              → the amazon building code (applied on-site)
 *   - `used_aci_grant`             → the string `'true'` | `'false'`
 *   - `grant_id`                   → Benevity grant-id format
 *   - `title`                      → free text
 *   - `source`                     → attribution only (analytics, not a form field)
 *
 * Everything here is a starting point only; every seeded field stays fully editable in the wizard.
 */

/** Query params this parser recognises. Anything not in this list is ignored. */
export const PREFILL_PARAM_KEYS = [
  'title',
  'focus',
  'activity',
  'activity_location_type',
  'building_code',
  'grant_id',
  'used_aci_grant',
  'source',
] as const;

/**
 * The subset of params that seed grant fields. The caller gates these behind the EventCreation flag,
 * so it needs to know which applied params to discount when the flag is off — otherwise the prefill
 * metric would report grant params as applied when they were dropped before reaching the form.
 */
export const GRANT_PREFILL_PARAM_KEYS: readonly string[] = ['grant_id', 'used_aci_grant'];

/** Attribution tag length cap (free text, kept short to stay well within QR/email URL budgets). */
export const SOURCE_MAX_LENGTH = 64;
/** No dedicated building-code field limit exists; building codes are short (e.g. SEA11). */
export const BUILDING_MAX_LENGTH = 30;

/** Maps the legacy `activity_location_type` values (used app-wide) to the LocationType enum. */
const LOCATION_TYPE_BY_LEGACY: Record<string, LocationType> = {
  'on-campus': LocationType.ON_SITE,
  'off-campus': LocationType.OFF_SITE,
  virtual: LocationType.VIRTUAL,
};

export interface EventPrefillResult {
  /** Partial event input to merge into the create-event initial state. */
  eventInput: Partial<EventInput>;
  /** Entry-point attribution tag (e.g. grant_approval, campaign_earth_day, qr_sea25). Not a form field. */
  source?: string;
  /** Names of the allowlisted params that passed validation and were applied (excludes source). */
  appliedParams: string[];
}

/**
 * Parse allowlisted prefill params into a partial EventInput. Never throws.
 *
 * Grant fields (`grant_id`/`used_aci_grant`) are parsed here but the caller is responsible for gating
 * them behind the EVENT_CREATION feature flag.
 */
export function parseEventPrefill(searchParams: URLSearchParams): EventPrefillResult {
  const eventInput: Partial<EventInput> = {};
  const appliedParams: string[] = [];

  // title — free text, capped to the existing field limit; never trust the link's length.
  const title = searchParams.get('title');
  if (title && title.trim()) {
    eventInput.title = title.slice(0, FIELD_LIMITS.EVENT_TITLE);
    appliedParams.push('title');
  }

  // focus → focusArea — must be a real eventOptionLists code, else drop.
  const focus = searchParams.get('focus');
  if (focus && FOCUS_AREAS.values.includes(focus)) {
    eventInput.focusArea = focus;
    appliedParams.push('focus');
  }

  // activity → primaryActivity — must be a real eventOptionLists code, else drop.
  const activity = searchParams.get('activity');
  if (activity && PRIMARY_ACTIVITIES.values.includes(activity)) {
    eventInput.primaryActivity = activity;
    appliedParams.push('activity');
  }

  // location: activity_location_type + building_code (on-site).
  // Mirrors useEditableEventInfo: building_code becomes amazonBuilding under on-campus. The
  // create-event LocationComponent then geocodes the seeded building on mount (VOLUNTEERCENTER-777)
  // to recover the coordinates/city/timezone the autosuggest would normally supply. When no location
  // type is given, a lone building_code implies on-site. offsite_free_text is intentionally NOT
  // supported: an off-site address can only be enriched via the Amazon Location Service place lookup,
  // which needs a PlaceId the URL cannot carry, so seeding a bare address would strand the event
  // without coordinates or timezone (see VOLUNTEERCENTER-777 review).
  const locTypeRaw = searchParams.get('activity_location_type');
  const buildingCode = searchParams.get('building_code');
  const mappedLocType = locTypeRaw ? LOCATION_TYPE_BY_LEGACY[locTypeRaw] : undefined;
  const cappedBuilding =
    buildingCode && buildingCode.trim() ? buildingCode.slice(0, BUILDING_MAX_LENGTH) : undefined;

  if (mappedLocType === LocationType.ON_SITE) {
    eventInput.location = { locationType: LocationType.ON_SITE, amazonBuilding: cappedBuilding };
    appliedParams.push('activity_location_type');
    if (cappedBuilding) appliedParams.push('building_code');
  } else if (mappedLocType) {
    // off-site or virtual: seed the location type only (no free-text address by design).
    eventInput.location = { locationType: mappedLocType };
    appliedParams.push('activity_location_type');
  } else if (cappedBuilding) {
    // building_code alone implies an on-site event (create-event's default is ON_SITE anyway).
    eventInput.location = { locationType: LocationType.ON_SITE, amazonBuilding: cappedBuilding };
    appliedParams.push('building_code');
  }

  // grants — parsed here; caller gates behind EVENT_CREATION. used_aci_grant is the string 'true'/'false'.
  const usedAciGrantRaw = searchParams.get('used_aci_grant');
  if (usedAciGrantRaw === 'true' || usedAciGrantRaw === 'false') {
    eventInput.usedAciGrant = usedAciGrantRaw === 'true';
    appliedParams.push('used_aci_grant');
  }
  const grantId = searchParams.get('grant_id');
  if (grantId && GRANT_ID_REGEX.test(grantId)) {
    eventInput.grantId = grantId;
    // A valid grant id IS the evidence that a grant was used, so it always wins: a link carrying both
    // `grant_id` and `used_aci_grant=false` is self-contradictory, and seeding the form with a grant id
    // but the flag off would leave the creator with an inconsistent (and invalid) grant section.
    // Parsed after used_aci_grant deliberately, so this assignment overrides it.
    eventInput.usedAciGrant = true;
    appliedParams.push('grant_id');
  }

  // source — attribution only, not a form field. Length-capped free text.
  const rawSource = searchParams.get('source');
  const source = rawSource && rawSource.trim() ? rawSource.slice(0, SOURCE_MAX_LENGTH) : undefined;

  // TODO(VC-777): description/shortDescription are excluded from v1. When seeding a description is
  // needed, do it via a `template=<id>` param that maps to a server-side template — never
  // URL-encode the description body (blows the QR/email URL-length budget).
  //
  // URL length: the safe ceiling for a whole URL is ~2000 characters. Past that, browsers, mail
  // clients and QR scanners may truncate or reject the link before it ever reaches Volunteer Center,
  // so the failure is invisible here and cannot be guarded against in this parser. Keep new params
  // few and short; anything long (descriptions, lists, rich text) belongs behind a server-side id.

  return { eventInput, source, appliedParams };
}
